import { Button, Spinner } from '@consigli/facade';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MeetingSummaryData } from '../types';

interface DownloadMenuProps {
  meetingData: MeetingSummaryData | null;
}

export const DownloadMenu: FC<DownloadMenuProps> = ({ meetingData }) => {
  const isDisabled = !meetingData?.isDataReady;
  const isExportingPdf = meetingData?.isExportingPdf ?? false;
  const isExportingDocx = meetingData?.isExportingDocx ?? false;
  const isExportingExcel = meetingData?.isExportingExcel ?? false;
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = async (exportFn?: () => Promise<void>) => {
    if (exportFn) {
      await exportFn();
    }
    setShowMenu(false);
  };

  const isAnyExporting = isExportingPdf || isExportingDocx || isExportingExcel;

  const menuItems = [
    { label: 'download-pdf', loading: isExportingPdf, onExport: meetingData?.handleExportPdf },
    { label: 'download-docx', loading: isExportingDocx, onExport: meetingData?.handleExportDocx },
    {
      label: 'download-excel',
      loading: isExportingExcel,
      onExport: meetingData?.handleExportExcel,
    },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <Button
        secondary
        rounded
        onClick={() => {
          if (!isDisabled) {
            setShowMenu(!showMenu);
          }
        }}
        disabled={isDisabled}
      >
        {t('meeting-secretary.meetings.download', 'Download')} ▾
      </Button>
      {showMenu && !isDisabled && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
          {menuItems.map((item) => (
            <button
              key={item.label}
              onClick={() => handleExport(item.onExport)}
              disabled={isAnyExporting}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>
                {item.loading
                  ? t('meeting-secretary.meetings.exporting', 'Exporting...')
                  : t(`meeting-secretary.meetings.${item.label}`)}
              </span>
              {item.loading && <Spinner size="xsmall" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
